import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DrawerShell } from 'src/components/atlas/molecules/Drawer/DrawerShell';
import { ConfirmationBeforeRemoveModal } from 'src/components/atlas/organisms/ConfirmationBeforeRemoveModal/confirmationBeforeRemoveModal';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { GetOrganisationCustomFieldSectionsQuery } from 'src/graphql/generated';
import { useCustomFieldSectionMutations } from './hooks/useCustomFieldSectionMutations';

const DEFAULT_TOGGLE_LABEL = 'Default section for new fields';
const DEFAULT_TOGGLE_HELPER =
  'New custom fields land in the current default section today. Turning this on moves that rule here — ungrouped and previous-default fields move to this section.';
const DEFAULT_ENABLE_CONFIRM_TITLE = 'Make this the default section?';
const DEFAULT_ENABLE_CONFIRM_BODY =
  'New custom fields will land here, and any ungrouped fields plus the fields in the current default section will move to this section. This does not delete any fields.';
const DEFAULT_UNSET_CONFIRM_TITLE = 'Remove default section?';
const DEFAULT_UNSET_CONFIRM_BODY =
  'New custom fields will no longer be assigned to a section automatically. Existing fields stay where they are.';

type CustomFieldSection =
  GetOrganisationCustomFieldSectionsQuery['getOrganisationCustomFieldSections']['sections'][number];

const MAX_NAME_LENGTH = 140;
const MAX_DESCRIPTION_LENGTH = 250;

interface CreateCustomFieldSectionSideSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mode?: 'create' | 'edit';
  sectionToEdit?: CustomFieldSection;
  setSectionToDelete?: (section: CustomFieldSection | undefined) => void;
}

export const CreateCustomFieldSectionSideSheet: React.FC<
  CreateCustomFieldSectionSideSheetProps
> = ({
  isOpen,
  onClose,
  onSuccess,
  mode = 'create',
  sectionToEdit,
  setSectionToDelete,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;

  const isEditMode = mode === 'edit';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [nameError, setNameError] = useState('');
  const [confirmDefaultOpen, setConfirmDefaultOpen] = useState(false);

  const { createSection, updateSection, isLoading } =
    useCustomFieldSectionMutations();

  const wasDefault = sectionToEdit?.isDefault ?? false;
  const sectionFields = sectionToEdit?.fields ?? [];
  const isTurningDefaultOn = isDefault && !wasDefault;
  const isTurningDefaultOff = !isDefault && wasDefault;

  // Depend on the section id (not the object) so an inline-derived sectionToEdit
  // whose identity changes each parent render doesn't re-run this reset.
  useEffect(() => {
    if (isOpen) {
      setName(sectionToEdit?.name ?? '');
      setDescription(sectionToEdit?.description ?? '');
      setIsDefault(sectionToEdit?.isDefault ?? false);
      setNameError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionToEdit?.id, isOpen]);

  const handleNameChange = (value: string) => {
    setName(value.slice(0, MAX_NAME_LENGTH));
    if (nameError) {
      setNameError('');
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setIsDefault(false);
    setNameError('');
    setConfirmDefaultOpen(false);
    onClose();
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Section name cannot be empty');
      return;
    }

    if (isTurningDefaultOn || isTurningDefaultOff) {
      setConfirmDefaultOpen(true);
      return;
    }

    await commitSave();
  };

  const handleConfirmDefault = async () => {
    setConfirmDefaultOpen(false);
    await commitSave();
  };

  const commitSave = async () => {
    const trimmedName = name.trim();

    try {
      if (isEditMode && sectionToEdit) {
        await updateSection({
          id: sectionToEdit.id,
          name: trimmedName,
          description: description.trim() || null,
          isDefault,
        });
        handleSuccessToast({ toastMessage: 'Section updated successfully' });
      } else {
        await createSection({
          name: trimmedName,
          description: description.trim() || null,
          isDefault,
        });
        handleSuccessToast({ toastMessage: 'Section created successfully' });
      }

      handleClose();
      onSuccess?.();
    } catch (error) {
      const message = (error as Error).message ?? '';
      if (message.toLowerCase().includes('already exists')) {
        setNameError('A section with this name already exists');
      }
      handleErrorToast({
        toastMessage: isEditMode
          ? 'Error updating section'
          : 'Error creating section',
      });
    }
  };

  const handleSectionDelete = () => {
    if (sectionToEdit && setSectionToDelete) {
      setSectionToDelete(sectionToEdit);
      handleClose();
    }
  };

  return (
    <DrawerShell
      open={isOpen}
      title={isEditMode ? 'Edit section' : 'Create section'}
      onRequestClose={handleClose}
      onNext={handleSave}
      nextDisabled={!name.trim() || isLoading}
      nextTitle={isEditMode ? 'Save changes' : 'Create section'}
      onBack={isEditMode ? handleClose : undefined}
      backTitle="Cancel"
      cancelTitle={isEditMode ? 'Delete section' : 'Cancel'}
      onCustomCancel={isEditMode ? handleSectionDelete : handleClose}
      cancelButtonState={isEditMode ? 'danger' : 'default'}
      cancelDisabled={isLoading || (isEditMode && sectionToEdit?.isDefault)}
      testId="create-custom-field-section-side-sheet"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Typography
          sx={{
            fontSize: '14px',
            color: color.type.muted.value,
            mt: '-12px',
          }}
        >
          Sections group custom fields on the case page. Fields appear in the
          order set here, inside the section they belong to.
        </Typography>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: '4px',
            }}
          >
            <Typography
              component="label"
              htmlFor="custom-field-section-name-input"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
              }}
            >
              Section name{' '}
              <span style={{ color: color.type.muted.value }}>*</span>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '12px', color: color.type.muted.value }}
            >
              {name.length}/{MAX_NAME_LENGTH}
            </Typography>
          </Box>
          <TextField
            id="custom-field-section-name-input"
            fullWidth
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Enter section name"
            data-testid="create-custom-field-section-name-input"
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            error={!!nameError}
          />
          {nameError && (
            <Typography
              sx={{
                fontSize: '12px',
                color: color.status.error.default.value,
                mt: '4px',
              }}
              data-testid="create-custom-field-section-name-error"
            >
              {nameError}
            </Typography>
          )}
        </Box>

        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: '4px',
            }}
          >
            <Typography
              component="label"
              htmlFor="custom-field-section-description-input"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
              }}
            >
              Description
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '12px', color: color.type.muted.value }}
            >
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </Typography>
          </Box>
          <TextField
            id="custom-field-section-description-input"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) =>
              setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))
            }
            placeholder="Explain what belongs in this section."
            data-testid="create-custom-field-section-description-input"
            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
          />
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                data-testid="create-custom-field-section-default-toggle"
              />
            }
            label={
              <Typography
                sx={{ fontSize: '14px', color: color.type.default.value }}
              >
                {DEFAULT_TOGGLE_LABEL}
              </Typography>
            }
          />
          <Typography
            sx={{
              fontSize: '12px',
              color: color.type.muted.value,
              mt: '4px',
            }}
            data-testid="create-custom-field-section-default-helper"
          >
            {DEFAULT_TOGGLE_HELPER}
          </Typography>
        </Box>

        {isEditMode && (
          <Box>
            {sectionFields.length > 0 && (
              <Box
                sx={{
                  backgroundColor: '#F7F7F7',
                  borderRadius: '8px',
                  padding: '20px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: tokens.semantic.fontWeight.emphasis.value,
                    color: color.type.default.value,
                    mb: '16px',
                  }}
                >
                  Fields listed in this section
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {sectionFields.map((field, index) => (
                    <Box key={field.id}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: color.type.default.value,
                          lineHeight: '20px',
                          py: '8px',
                        }}
                        data-testid={`edit-section-field-${field.id}`}
                      >
                        {field.label}
                      </Typography>
                      {index < sectionFields.length - 1 && (
                        <Box
                          sx={{
                            height: '1px',
                            backgroundColor: '#E5E5E5',
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ConfirmationBeforeRemoveModal
        open={confirmDefaultOpen}
        title={
          isTurningDefaultOff
            ? DEFAULT_UNSET_CONFIRM_TITLE
            : DEFAULT_ENABLE_CONFIRM_TITLE
        }
        content={
          isTurningDefaultOff
            ? DEFAULT_UNSET_CONFIRM_BODY
            : DEFAULT_ENABLE_CONFIRM_BODY
        }
        confirmButtonText={
          isTurningDefaultOff ? 'Remove default' : 'Make default'
        }
        onConfirm={handleConfirmDefault}
        onCancel={() => setConfirmDefaultOpen(false)}
        onClose={() => setConfirmDefaultOpen(false)}
      />
    </DrawerShell>
  );
};
